import { useEffect, useMemo } from 'react';
import {
    type Path,
    type PathValue,
    type UseFormReturn,
    useWatch,
} from 'react-hook-form';
import type { UnionToIntersection } from 'type-fest';
import type { ExtractAllEnumKeys } from '../types/extractKeysOfType';
import type { SharedUnionFieldsNested } from '../types/sharedUnionFieldsNested';
import type { FormName } from '../utils/form/getFormNames';

type StringAfterLastDot<S extends string> = S extends `${string}.${infer T}`
    ? T
    : S;

type SubtypePathsOfAllSubtypes<O extends object> = Exclude<
    Path<O>,
    FormName<O>
>;
type SubtypePathsOfSubtype<
    O extends object,
    D extends ExtractAllEnumKeys<O>,
    T extends string,
> = Exclude<Path<Extract<O, Record<D, T>>>, Path<SharedUnionFieldsNested<O>>>;

export type DefaultSubtypeValues<O extends object> = UnionToIntersection<
    {
        [k in SubtypePathsOfAllSubtypes<O>]: Record<
            StringAfterLastDot<k>,
            PathValue<O, k>
        >;
    }[SubtypePathsOfAllSubtypes<O>]
>;

export type SubtypeProperties<
    O extends object,
    D extends ExtractAllEnumKeys<O> & keyof O,
> = {
    [discriminator in O[D] & string]: {
        [k in StringAfterLastDot<
            SubtypePathsOfSubtype<O, D, discriminator>
        >]: k;
    };
};

export type SubtypeFormName<O extends object> = StringAfterLastDot<
    SubtypePathsOfAllSubtypes<O>
>; // TODO & Path<O>;
export type SubtypeFormNames<O extends object> = {
    [sfn in SubtypeFormName<O>]?: sfn;
};

export const useSubtypeFormNames = <
    V extends object,
    P extends Path<V> & ExtractAllEnumKeys<V> & string,
    PV extends PathValue<V, P>,
>({
    form,
    typeName,
    subtypeProperties,
    defaultSubtypeValues,
}: {
    form: UseFormReturn<V>;
    typeName: P;
    subtypeProperties: Record<PV, SubtypeFormNames<V>>;
    defaultSubtypeValues: DefaultSubtypeValues<V>;
}) => {
    const typeValue = useWatch({ control: form.control, name: typeName });
    const subtypeFormNames = subtypeProperties[typeValue];

    const nestingPrefix = useMemo(() => {
        const subStrings = typeName.split('.');
        subStrings.pop();
        subStrings.push('');
        return subStrings.join('.');
    }, [typeName]);

    useEffect(() => {
        const currentTypeValue = form.getValues(typeName);
        const currentSubtypeFormNames = subtypeProperties[currentTypeValue];
        Object.entries(defaultSubtypeValues).forEach(
            ([flatKey, defaultValue]) => {
                const nestedKey = `${nestingPrefix}${flatKey}` as Path<V>;
                const currentValue = form.getValues(nestedKey);
                if (!(flatKey in currentSubtypeFormNames)) {
                    if (currentValue !== undefined) {
                        form.control.unregister(nestedKey);
                    }
                } else if (currentValue === undefined) {
                    // this has to be an explicit === undefined check
                    form.setValue(
                        nestedKey,
                        defaultValue as PathValue<V, Path<V>>
                    );
                }
            }
        );
    }, [defaultSubtypeValues, form.control, nestingPrefix, typeValue, typeName]);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.fromEntries(
        Object.keys(subtypeFormNames).map((flatKey) => [
            flatKey,
            `${nestingPrefix}${flatKey}`,
        ])
    ) as typeof subtypeFormNames;
};
